/* START OF FILE js/GameCore.js */
// js/GameCore.js

// Import dependencies
import StorageManager from './StorageManager.js';
import { SqudlePuzzle } from './core/puzzleClass.js';
import { createEmptyGrid, cloneGrid } from './utils/gridHelpers.js';
import { scrambleGrid } from './core/scrambling.js';
import PlayerStats from './stats.js'; // Import PlayerStats for type hinting and internal use
import { touchSessionOnSwap } from './telemetry.js';

/**
 * GameCore.js - Core game logic and state management
 */

const RETRY_DELAYS = [5000, 15000, 30000, 60000];
const MAX_ATTEMPTS = 5;
const FETCH_TIMEOUT_MS = 10000;
const REVALIDATE_INTERVAL_MS = 10 * 60 * 1000;
const VISIBILITY_REVALIDATE_MIN_MS = 60 * 1000;

export default class GameCore { // Add export default
  /**
   * @param {StorageManager} storageManager
   */
  constructor(storageManager) {
    if (!(storageManager instanceof StorageManager)) {
      console.error("GameCore requires a valid StorageManager instance.");
      // Handle error appropriately, maybe throw or create a default
      this.storageManager = new StorageManager();
    } else {
      this.storageManager = storageManager;
    }

    /** @type {SqudlePuzzle | null} */
    this.puzzle = null;
    const settings = this.storageManager.loadSettings(); // Load settings once
    this.gridSize = settings.gridSize || 4;
    // Initialize isHardMode based on preference initially, but initGame will override if restoring state
    this.isHardMode = settings.hardModePrefs?.[this.gridSize] || false;
    this.isUnlimitedMode = !this.isHardMode; // Set based on initial hard mode pref

    this.selectedTile = null;
    this.hintUsed = false;
    this.restored = false;
    this.challengeToken = null; // To store the token from the backend
    this._fetchState = 'idle';
    this._retryTimerId = null;
    this._abortController = null;
    this._fetchGeneration = 0;
    this._revalidation = null;
    this._revalidateController = null;
    this._revalidateTimerId = null;
    this._lastRevalidatedAt = 0;
    this._onVisibilityChange = null;
    this._attempt = 0;
    this._redirectedFrom = null;
    this._double503 = false;

    this.MAX_SWAPS = this.storageManager.getMaxSwapsForGridSize(this.gridSize);

    console.log(
      `GameCore constructed: Initial Size=${this.gridSize}, Initial HM Pref=${this.isHardMode}`
    );
  }

  /** Single source of truth for the puzzle day: the server-authoritative date held by StorageManager. */
  get todayDate() {
    return this.storageManager.todayDate;
  }

  set todayDate(value) {
    this.storageManager.todayDate = value;
  }

  setGridSize(gridSize) {
    const newSize = parseInt(gridSize);
    if (isNaN(newSize) || newSize < 4 || newSize > 5) {
      console.warn(`Invalid grid size requested: ${gridSize}. Keeping ${this.gridSize}.`);
      return;
    }
    if (this.gridSize !== newSize) {
      console.log(`[setGridSize] Switching from ${this.gridSize} to ${newSize}`);
      this.gridSize = newSize;
      this.MAX_SWAPS = this.storageManager.getMaxSwapsForGridSize(this.gridSize);

      console.log(`GameCore: Grid size set to ${this.gridSize}. MAX_SWAPS: ${this.MAX_SWAPS}. HM state will be determined by initGame.`);

      // Save only the grid size preference
      this.storageManager.saveSettings({ gridSize: this.gridSize });
    }
  }

  async initGame() {
    console.log(`[initGame] Initializing for ${this.gridSize}x${this.gridSize}...`);
    this.selectedTile = null;
    this.hintUsed = false;
    this.restored = false;
    this._fetchState = 'idle';
    this.puzzle = null;
    this._fetchGeneration++; // drop any in-flight revalidation from a previous init
    this._cancelRevalidation();

    // Attempt to restore state
    const restoredState = this.restoreGameState();

    if (restoredState) {
      this.restored = true;
      console.log(`[initGame] State RESTORED for ${this.gridSize}x${this.gridSize}.`);
      this.isHardMode = restoredState.isHardMode;
      this.isUnlimitedMode = !this.isHardMode;
      this.hintUsed = restoredState.hintUsed || false;
      console.log(`[initGame] Restored: HM=${this.isHardMode}, Swaps=${this.puzzle.swapCount}`);

      if (restoredState.completed) {
        console.log(`[initGame] Restored state is already completed.`);
        if (!this.puzzle.isSolved()) {
          this.puzzle.grid = cloneGrid(this.puzzle.solvedGrid);
        }
      }
      this.puzzle.logPuzzleSolution();
      this.revalidate();
      return { restored: true };
    }

    // No state to restore — caller must fetch via fetchAndInitPuzzle()
    console.log(`[initGame] No valid state to restore for ${this.gridSize}x${this.gridSize}.`);
    this.hintUsed = false;
    this.challengeToken = null;

    if (this.hasCompletedTodaysPuzzle()) {
      this.isHardMode = false;
      this.isUnlimitedMode = true;
    } else {
      const settings = this.storageManager.loadSettings();
      this.isHardMode = settings.hardModePrefs?.[this.gridSize] || false;
      this.isUnlimitedMode = !this.isHardMode;
    }

    return { restored: false };
  }

  /**
   * Ask the server which day it is and replace the shown puzzle if it belongs to
   * a different day; on the same day only the challenge token is refreshed.
   * Runs after a restore and whenever watchForNewDay() fires. One at a time,
   * never while a puzzle fetch is in progress. Offline or on any failure the
   * current state is kept silently.
   */
  revalidate() {
    if (this._revalidation) return this._revalidation;
    if (!this._canRevalidate()) return Promise.resolve(false);
    const run = this._revalidateOnce()
      .catch((e) => { console.error('[revalidate]', e); return false; })
      .finally(() => { if (this._revalidation === run) this._revalidation = null; });
    this._revalidation = run;
    return run;
  }

  _canRevalidate() {
    if (!navigator.onLine || !this.puzzle) return false;
    return this._fetchState === 'loaded' || (this.restored && this._fetchState === 'idle');
  }

  _cancelRevalidation() {
    if (this._revalidateController) this._revalidateController.abort();
    this._revalidateController = null;
    this._revalidation = null;
  }

  async _revalidateOnce() {
    const generation = this._fetchGeneration;
    const stateDate = this.todayDate;
    const controller = new AbortController();
    this._revalidateController = controller;
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    let data;
    try {
      data = await this.fetchDailyPuzzle(controller.signal);
    } finally {
      clearTimeout(timeoutId);
      if (this._revalidateController === controller) this._revalidateController = null;
    }
    if (generation !== this._fetchGeneration || !data || data.status503) return false;
    this._lastRevalidatedAt = Date.now();
    this.storageManager.persistServerDate(data.puzzleDate);
    if (data.puzzleDate === stateDate) {
      this._adoptFreshToken(data.challengeToken);
      return false;
    }
    this._replaceStaleState(data);
    return true;
  }

  /** Challenge tokens expire after 30 min, so a same-day revalidation keeps the saved one current. */
  _adoptFreshToken(token) {
    if (typeof token !== 'string' || !token || this.hasCompletedTodaysPuzzle()) return;
    this.challengeToken = token;
    this.saveGameState();
  }

  /**
   * An open tab can cross into the next puzzle day: revalidate on becoming visible
   * (at most once a minute after a success, since app switching can be frequent)
   * and every 10 minutes while visible.
   */
  watchForNewDay() {
    if (this._onVisibilityChange) return;
    this._onVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      if (visible && Date.now() - this._lastRevalidatedAt >= VISIBILITY_REVALIDATE_MIN_MS) this.revalidate();
      this._setRevalidateTimer(visible);
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this._setRevalidateTimer(document.visibilityState === 'visible');
  }

  _setRevalidateTimer(on) {
    clearInterval(this._revalidateTimerId);
    this._revalidateTimerId = on ? setInterval(() => this.revalidate(), REVALIDATE_INTERVAL_MS) : null;
  }

  _replaceStaleState(data) {
    const size = this.gridSize;
    this.storageManager.clearGameState(size);
    this.restored = false;
    this.selectedTile = null;
    this.hintUsed = false;
    this.todayDate = data.puzzleDate;
    this.storageManager.clearStaleData();
    const settings = this.storageManager.loadSettings();
    this.isHardMode = this.hasCompletedTodaysPuzzle() ? false : (settings.hardModePrefs?.[size] || false);
    this.isUnlimitedMode = !this.isHardMode;
    this._createPuzzleFromData(data);
    this._fetchState = 'loaded';
    document.dispatchEvent(new CustomEvent('puzzle-date-changed', { detail: { date: data.puzzleDate } }));
    this._dispatchFetchStatus('loaded');
  }

  _createPuzzleFromData(data) {
    this.challengeToken = data.challengeToken;
    this.puzzle = new SqudlePuzzle(this.gridSize);
    this.puzzle.solvedGrid = data.solutionDetail.rows.map(word => word.split(''));
    this.puzzle.grid = cloneGrid(this.puzzle.solvedGrid);

    if (this.hasCompletedTodaysPuzzle()) {
      this.puzzle.swapCount = 0;
      console.log("[_createPuzzleFromData] Displaying completed puzzle.");
    } else {
      scrambleGrid(this.puzzle.grid, this.puzzle.solvedGrid, this.puzzle.validWordSet, this.puzzle.random.bind(this.puzzle));
      this.puzzle.swapCount = 0;
      console.log("[_createPuzzleFromData] Fetched and scrambled new puzzle.");
      this.saveGameState();
    }
    this.puzzle.logPuzzleSolution();
  }

  _dispatchFetchStatus(status, attempt) {
    document.dispatchEvent(new CustomEvent('puzzle-fetch-status', {
      detail: {
        status,
        attempt: attempt ?? this._attempt,
        maxAttempts: MAX_ATTEMPTS,
        error: null
      }
    }));
  }

  async fetchAndInitPuzzle() {
    // Entry guard: safe to call from any state
    if (this._fetchState !== 'idle') {
      if (this._abortController) this._abortController.abort();
      if (this._retryTimerId) clearTimeout(this._retryTimerId);
      this._retryTimerId = null;
    }

    this._fetchGeneration++;
    const myGeneration = this._fetchGeneration;
    this._attempt = 1;
    this._fetchState = 'fetching';

    this._dispatchFetchStatus('fetching');
    await this._executeFetch(myGeneration);
  }

  async _executeFetch(generation) {
    // Pre-fetch offline check (layer 1)
    if (!navigator.onLine) {
      this._fetchState = 'offline';
      this._dispatchFetchStatus('offline');
      return;
    }

    // Create AbortController with 10s timeout
    this._abortController = new AbortController();
    const timeoutId = setTimeout(() => this._abortController.abort(), FETCH_TIMEOUT_MS);

    try {
      const data = await this.fetchDailyPuzzle(this._abortController.signal);
      clearTimeout(timeoutId);

      // Stale generation check
      if (generation !== this._fetchGeneration) return;

      // 503 redirect handling (AC1, AC6)
      if (data && data.status503) {
        if (this._redirectedFrom !== null) {
          // Double-503: both sizes exhausted (AC6)
          this._double503 = true;
          this._fetchState = 'failed';
          this._redirectedFrom = null;
          this._dispatchFetchStatus('failed');
          return;
        }
        const alternateSize = this.gridSize === 4 ? 5 : 4;
        this._redirectedFrom = this.gridSize;
        this._fetchState = 'failed';
        document.dispatchEvent(new CustomEvent('puzzle-fetch-redirect', {
          detail: { fromSize: this.gridSize, toSize: alternateSize, reason: 'pool_empty' }
        }));
        return;
      }

      if (!data) throw new Error('Fetch returned null');

      // Success
      this._abortController = null;
      this._retryTimerId = null;
      this._redirectedFrom = null;
      this._double503 = false;

      // Propagate server date BEFORE creating puzzle (saveGameState needs correct date)
      this.todayDate = data.puzzleDate;
      this.storageManager.persistServerDate(data.puzzleDate);

      this._createPuzzleFromData(data);
      this._fetchState = 'loaded';
      this._dispatchFetchStatus('loaded');

    } catch (error) {
      clearTimeout(timeoutId);
      this._abortController = null;

      // Stale generation check
      if (generation !== this._fetchGeneration) return;

      // Post-error offline check (layer 2)
      if (!navigator.onLine) {
        this._fetchState = 'offline';
        this._dispatchFetchStatus('offline');
        return;
      }

      if (this._attempt >= MAX_ATTEMPTS) {
        this._fetchState = 'failed';
        this._dispatchFetchStatus('failed');
      } else {
        this._fetchState = 'retrying';
        this._dispatchFetchStatus('retrying');
        const delayIndex = this._attempt - 1;
        this._attempt++;
        this._retryTimerId = setTimeout(
          () => this._executeFetch(generation),
          RETRY_DELAYS[delayIndex]
        );
      }
    }
  }

  clearRetryState() {
    if (this._abortController) this._abortController.abort();
    this._abortController = null;
    if (this._retryTimerId) clearTimeout(this._retryTimerId);
    this._retryTimerId = null;
    this._attempt = 0;
    this._fetchState = 'idle';
    this._redirectedFrom = null;
    this._double503 = false;
  }


  consumeDouble503() {
    const val = this._double503;
    this._double503 = false;
    return val;
  }

  setOfflineState() {
    this._fetchState = 'offline';
  }

  handleOffline() {
    if (this._fetchState === 'fetching' || this._fetchState === 'retrying') {
      const currentAttempt = this._attempt;
      if (this._abortController) this._abortController.abort();
      this._abortController = null;
      if (this._retryTimerId) clearTimeout(this._retryTimerId);
      this._retryTimerId = null;
      this._attempt = currentAttempt;
      this._fetchState = 'offline';
      this._dispatchFetchStatus('offline');
    }
  }


  /**
   * Sets the Hard Mode state.
   * @param {boolean} enabled - Whether to enable Hard Mode.
   * @param {boolean} force - If true, bypasses checks (e.g., user confirmation). Used internally.
   * @returns {boolean} True if the state was successfully set or already correct, false otherwise.
   */
  setHardMode(enabled, force = false) {
    console.log(`[setHardMode] Attempting to set Hard Mode to: ${enabled}. Current: ${this.isHardMode}. Force: ${force}. GridSize: ${this.gridSize}`);

    // 1. Check if already in the desired state (unless forcing)
    if (!force && this.isHardMode === enabled) {
      console.log(`[setHardMode] Hard Mode is already ${enabled}. No change.`);
      return true;
    }

    // 2. Prevent enabling mid-game
    if (enabled && this.puzzle && this.puzzle.swapCount > 0) {
      console.warn('[setHardMode] Cannot enable Hard Mode after the first swap.');
      document.dispatchEvent(new CustomEvent('hardmode-toggle-failed', { detail: { reason: 'already_started' } }));
      return false;
    }

    // 3. Prevent enabling if puzzle is completed
    if (enabled && (this.hasCompletedTodaysPuzzle() || (this.puzzle && this.puzzle.isSolved()))) {
      console.warn(`[setHardMode] Cannot enable Hard Mode for ${this.gridSize}x${this.gridSize}, puzzle already completed today.`);
      document.dispatchEvent(new CustomEvent('hardmode-toggle-failed', { detail: { reason: 'already_completed' } }));
      return false;
    }

    // --- If trying to disable hard mode after starting, it becomes unlimited ---
    if (!force && !enabled && this.puzzle && this.puzzle.swapCount > 0) {
        console.log('[setHardMode] Disabling Hard Mode mid-game, enabling unlimited swaps.');
        this.isUnlimitedMode = true; // Explicitly enable unlimited when HM is turned off mid-game
    } else {
        this.isUnlimitedMode = !enabled; // Set unlimited based on the *new* HM state
    }
    this.isHardMode = enabled; // Set the hard mode state
    console.log(`[setHardMode] Hard Mode state is now: ${this.isHardMode}, Unlimited Mode state is now: ${this.isUnlimitedMode}`);

    // 6. Save the preference for this grid size
    const currentSettings = this.storageManager.loadSettings();
    const prefs = currentSettings.hardModePrefs || {};
    prefs[this.gridSize] = this.isHardMode; // Update preference for current size
    this.storageManager.saveSettings({ ...currentSettings, hardModePrefs: prefs });
    console.log(`[setHardMode] Saved Hard Mode preference for ${this.gridSize}x${this.gridSize}: ${this.isHardMode}`);

    // 7. Save game state if change happens before first swap (or if forced)
    if (this.puzzle && (this.puzzle.swapCount === 0 || force)) {
        this.saveGameState();
        console.log(`[setHardMode] Game state saved after HM change (Swaps: ${this.puzzle.swapCount}, Force: ${force})`);
    }

    // 8. Notify UI about the state change
    document.dispatchEvent(new CustomEvent('hardmode-state-changed', { detail: { isHardMode: this.isHardMode } }));

    return true; // Indicate success
  }


  swapTiles(row1, col1, row2, col2) {
    if (!this.puzzle || !this.puzzle.grid) {
      console.error("Swap failed: Puzzle or grid not initialized.");
      return { success: false };
    }
    if (this.hasCompletedTodaysPuzzle()) {
      console.log("Swap blocked: Puzzle already completed today.");
      return { success: false };
    }

    if (this.isHardMode && this.puzzle.swapCount >= this.MAX_SWAPS) {
      console.warn('Swap prevented: Swap limit reached (Hard Mode).');
      if (this.puzzle && !this.puzzle.isSolved()) {
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent('hardmode-failed', { detail: { reason: 'swap_limit' } }));
        }, 50);
      }
      return { success: false };
    }

    const oldState = this.getValidationState();
    const success = this.puzzle.swapCells(row1, col1, row2, col2);

    if (success) {
      touchSessionOnSwap();
      const newState = this.getValidationState();
      this.saveGameState(); // Save after successful swap

      if (this.isHardMode && this.puzzle.swapCount >= this.MAX_SWAPS && !this.puzzle.isSolved()) {
        console.log('Hard Mode swap limit reached exactly after this swap.');
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('hardmode-failed', { detail: { reason: 'swap_limit' } }));
        }, 50);
      }
      else if (this.puzzle.isSolved()) {
        console.log('Puzzle solved after swap!');
        if (this.playerStats) {
            this.saveCompletedPuzzle(this.playerStats);
        } else {
            console.error("Cannot save completed puzzle: PlayerStats instance not available in GameCore.");
        }
        document.dispatchEvent(
          new CustomEvent('puzzle-solved', {
            detail: {
              swapCount: this.puzzle.swapCount,
              isUnlimitedMode: this.isUnlimitedMode,
              isHardMode: this.isHardMode
            },
          })
        );
      }
      return { success: true, oldState: oldState, newState: newState };
    }
    return { success: false };
  }

  getHint() {
    if (this.isHardMode) {
      console.log("Hint blocked: Hard mode is active.");
      return null;
    }
    if (this.hintUsed) {
      console.log("Hint blocked: Hint already used today.");
      return null;
    }
    if (!this.puzzle || this.puzzle.isSolved()) {
      console.log("Hint blocked: Puzzle not loaded or already solved.");
      return null;
    }
    if (this.hasCompletedTodaysPuzzle()) {
      console.log("Hint blocked: Puzzle already completed.");
      return null;
    }
    if (typeof this.puzzle.getHint !== 'function') {
      console.error("Hint failed: puzzle.getHint is not a function.");
      return null;
    }
    return this.puzzle.getHint();
  }

  applyHint() {
    const hint = this.getHint();
    if (hint) {
      if (!this.puzzle || typeof this.puzzle.swapCells !== 'function') {
          console.error("Hint application failed: Puzzle or swapCells method missing.");
          return null;
      }

      this.hintUsed = true; // Mark hint used *before* swap

      const swapResult = this.swapTiles(hint.sourceRow, hint.sourceCol, hint.targetRow, hint.targetCol);

      if (!swapResult.success) {
        console.warn("Hint application failed: Internal swap failed. Reverting hintUsed status.");
        this.hintUsed = false; // Revert status
        this.saveGameState(); // Save reverted state
        return null;
      }

      this.puzzle.swapCount += 2; // Apply penalty (swapTiles added 1, this adds 2 more)
      console.log(`Hint applied. Swap count increased by 3 to ${this.puzzle.swapCount}`);
      this.saveGameState(); // Save state with penalty

      document.dispatchEvent(new CustomEvent('hint-applied', { detail: { hint: hint } }));
      return hint;
    }
    return null;
  }

  enableUnlimitedMode() {
    if (!this.isUnlimitedMode) {
      console.log('[enableUnlimitedMode] Enabling Unlimited mode...');
      this.setHardMode(false, true); // Force disable hard mode
    } else {
        console.log('[enableUnlimitedMode] Unlimited mode is already active.');
    }
  }

  getValidationState() {
    if (this.puzzle && typeof this.puzzle.getValidationState === 'function') {
      return this.puzzle.getValidationState();
    }
    console.warn("[getValidationState] Puzzle not ready, returning empty state.");
    return createEmptyGrid(this.gridSize, 0);
  }

  isSolved() {
    if (this.hasCompletedTodaysPuzzle()) return true;
    return this.puzzle ? this.puzzle.isSolved() : false;
  }

  hasCompletedTodaysPuzzle(gridSize = this.gridSize, date = this.todayDate) {
    return this.storageManager.hasCompletedTodaysPuzzle(gridSize, date);
  }

  saveGameState() {
    if (!this.puzzle) {
      console.warn("[GameCore.saveGameState] Cannot save: Puzzle not initialized.");
      return false;
    }
    return this.storageManager.saveGameState(
      this.puzzle,
      this.isHardMode,
      this.hasCompletedTodaysPuzzle(),
      this.gridSize,
      this.challengeToken
    );
  }

  /**
   * Saves the completed puzzle state via StorageManager and triggers stat recording via PlayerStats.
   * @param {PlayerStats} playerStats - The PlayerStats instance to record completion.
   * @returns {boolean} True if the completion was successfully saved by StorageManager.
   */
  saveCompletedPuzzle(playerStats) { // Accept PlayerStats instance
    if (!this.puzzle) {
      console.error("[GameCore.saveCompletedPuzzle] Cannot save: Puzzle not initialized.");
      return false;
    }
    if (this.hasCompletedTodaysPuzzle()) {
      console.warn("[GameCore.saveCompletedPuzzle] Save skipped: Already marked completed for today.");
      return false;
    }

    console.log(
      `[GameCore.saveCompletedPuzzle] Attempting save. Size: ${this.gridSize}, Swaps: ${this.puzzle.swapCount}, HM: ${this.isHardMode}`
    );

    // StorageManager determines leaderboard eligibility based on mode and swap count
    const success = this.storageManager.saveCompletedPuzzle(
      this.puzzle,
      this.isHardMode,
      this.gridSize,
      this.challengeToken
    );

    // --- Stat Recording ---
    if (success) {
      console.log(`[GameCore.saveCompletedPuzzle] Puzzle solved & completion saved. Attempting to record stats for size ${this.gridSize}...`);
      if (playerStats && typeof playerStats.recordCompletion === 'function') {
        playerStats.recordCompletion(this.todayDate, this.puzzle.swapCount, this.gridSize, this.isHardMode);
        console.log(`[GameCore.saveCompletedPuzzle] Called playerStats.recordCompletion for size ${this.gridSize}. Mode Recorded: ${this.isHardMode ? 'Hard' : 'Regular/Unlimited'}`);
      } else {
        console.warn('[GameCore.saveCompletedPuzzle] PlayerStats instance not provided or missing method, cannot update stats.');
      }
    } else {
       console.error("[GameCore.saveCompletedPuzzle] Failed to save completion status via StorageManager. Stats not recorded.");
    }
    // --- End Stat Recording ---

    this.isHardMode = false;
    this.isUnlimitedMode = true;
    this.hintUsed = false;
    // Don't clear challenge token here, it might be needed if leaderboard submission fails/retries
    console.log(`[GameCore.saveCompletedPuzzle] Internal mode flags reset after completion: HM=${this.isHardMode}, Unlimited=${this.isUnlimitedMode}`);

    return success;
  }


  restoreGameState() {
    const savedState = this.storageManager.loadGameState(this.gridSize);
    if (savedState) {
       // Ensure puzzle instance exists before trying to restore into it
       if (!this.puzzle) {
           this.puzzle = new SqudlePuzzle(this.gridSize);
           console.log(`[GameCore.restoreGameState] Created SqudlePuzzle instance for size ${this.gridSize} before restoring.`);
       }
      try {
        // Restore core puzzle properties
        this.puzzle.grid = savedState.grid;
        this.puzzle.solvedGrid = savedState.solvedGrid;
        this.puzzle.swapCount = savedState.swapCount;
        this.puzzle.hintUsed = savedState.hintUsed || false;
        this.challengeToken = savedState.challengeToken || null; // Restore challenge token

        console.log(`[GameCore.restoreGameState] Successfully parsed state for ${this.gridSize}x${this.gridSize}. Returning state object.`);
        return savedState; // Return the full loaded state object for initGame to use
      } catch (e) {
        console.error(`[GameCore.restoreGameState] Error applying restored state for ${this.gridSize}x${this.gridSize}:`, e);
        this.storageManager.clearGameState(this.gridSize); // Clear corrupted state
      }
    }
    return null; // Explicitly return null if no valid state found
  }

  logPuzzleSolution() {
    if (!this.puzzle || typeof this.puzzle.logPuzzleSolution !== 'function') {
      console.error("[GameCore.logPuzzleSolution] Cannot log solution: Puzzle object or log method missing.");
      return;
    }
    this.puzzle.logPuzzleSolution();
  }

  debugValidation() {
    if (!this.puzzle) {
      console.warn("[GameCore.debugValidation] Debug validation skipped: Puzzle not initialized.");
      return null;
    }
    const results = {
      isSolved: this.isSolved(),
      validationState: this.getValidationState(),
      swapCount: this.puzzle.swapCount,
      isHardMode: this.isHardMode,
      isUnlimitedMode: this.isUnlimitedMode,
      hintUsed: this.hintUsed,
      challengeToken: this.challengeToken // Include token in debug
    };
    console.log('[GameCore.debugValidation] Debug Validation Results:', results);
    return results;
  }

  // Add playerStats property if needed for saveCompletedPuzzle
  setPlayerStats(playerStatsInstance) {
      if (playerStatsInstance instanceof PlayerStats) {
          this.playerStats = playerStatsInstance;
      } else {
          console.error("Invalid PlayerStats instance provided to GameCore.");
      }
  }

  /**
   * Fetches the daily puzzle data from the backend worker.
   * @returns {Promise<{solutionDetail: {rows: string[], columns: string[]}, challengeToken: string, puzzleDate: string} | null>}
   */
  async fetchDailyPuzzle(signal) {
    const sizeKey = `${this.gridSize}x${this.gridSize}`;
    const WORKER_URL = 'https://api.squdle.com';
    const url = `${WORKER_URL}/puzzle/${sizeKey}`;
    console.log(`[fetchDailyPuzzle] Fetching puzzle from: ${url}`);

    try {
      const response = await fetch(url, signal ? { signal } : undefined);

      if (response.status === 503) {
        return { status503: true };
      }

      if (!response.ok) {
        let errorMsg = `Error fetching puzzle: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            errorMsg += ` - ${errorData.error || 'Unknown backend error'}`;
        } catch (e) { /* Ignore JSON parsing error */ }
        console.error(`[fetchDailyPuzzle] ${errorMsg}`);
        throw new Error(errorMsg); // Throw to be caught below
      }

      const data = await response.json();

      // Validate response structure more carefully
       if (!data || typeof data !== 'object') {
         throw new Error('Invalid response format: Expected an object.');
       }
       if (typeof data.challengeToken !== 'string') {
         throw new Error('Invalid response format: Missing or invalid challengeToken.');
       }
       if (typeof data.puzzleDate !== 'string') {
         throw new Error('Invalid response format: Missing or invalid puzzleDate.');
       }
       if (!/^\d{4}-\d{2}-\d{2}$/.test(data.puzzleDate)) {
         throw new Error('Invalid response format: puzzleDate is not YYYY-MM-DD.');
       }
       if (!data.puzzleData || typeof data.puzzleData !== 'object') {
         throw new Error('Invalid response format: Missing or invalid puzzleData object.');
       }
       if (!data.puzzleData.solution || typeof data.puzzleData.solution !== 'object') {
          throw new Error('Invalid response format: Missing or invalid puzzleData.solution object.');
       }
       if (!Array.isArray(data.puzzleData.solution[sizeKey]) || data.puzzleData.solution[sizeKey].length === 0) {
           throw new Error(`Invalid response format: Missing or empty solution array for size ${sizeKey}.`);
       }
       const solutionDetail = data.puzzleData.solution[sizeKey][0];
       if (!solutionDetail || !Array.isArray(solutionDetail.rows)) {
           throw new Error(`Invalid response format: Missing or invalid solution detail rows for size ${sizeKey}.`);
       }
       // Optionally check for columns if they are expected
       // if (!solutionDetail.columns || !Array.isArray(solutionDetail.columns)) {
       //     console.warn(`[fetchDailyPuzzle] Columns data missing or invalid for size ${sizeKey}.`);
       //     // Decide if this is an error or acceptable
       // }


      const challengeToken = data.challengeToken;
      const puzzleDate = data.puzzleDate;

      console.log(`[fetchDailyPuzzle] Successfully fetched puzzle data and challenge token for ${sizeKey}.`);
      // Return only the necessary parts
      return { solutionDetail: { rows: solutionDetail.rows, columns: solutionDetail.columns || [] }, challengeToken, puzzleDate };

    } catch (error) {
      console.error('[fetchDailyPuzzle] Network or parsing error fetching puzzle:', error);
      return null; // Caller (initGame) handles error dispatch
    }
  }

} // End of GameCore class
// Removed window export
/* END OF FILE js/GameCore.js */
